import React from 'react'
import cx from 'classnames'
import { observer, useObserver } from 'mobx-react'
import { ColumnId } from '../ApplicantManagementTable/ApplicantManagementTableColumns'
import WorkspaceApplicant, {
  WorkspaceApplicantStatus,
} from 'admin/staffing/WorkspaceApplicant'
import Button from 'components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import {
  CheckboxCell,
  ImageCell,
  TableCell,
  TableRow,
  TextCell,
} from 'components/Table'
import ApplicantManagementPanelViewModel from '../ApplicantManagementPanel/ApplicantManagementPanelViewModel'
import styles from './ApplicantManagementTableRow.sass'

interface ApplicantManagementTableRowProps {
  vm: ApplicantManagementPanelViewModel
  applicant: WorkspaceApplicant
}

export default observer(function ApplicantManagementTableRow({
  vm,
  applicant,
}: ApplicantManagementTableRowProps) {
  const globalJobCapacity = applicant.provider.globalJobCapacity
  const globalCapacityString =
    globalJobCapacity > 0 ? globalJobCapacity.toString() : '∞'

  return (
    <TableRow data-test={`applicant-manager-row-${applicant.userPublicId}`}>
      {vm.columns.map((column) => {
        switch (column.id) {
          case ColumnId.Checkbox:
            return (
              <CheckboxCell
                key={column.id}
                checked={vm.isSelected(applicant)}
                onChange={() => vm.toggleApplicant(applicant)}
                dataTest={`applicant-manager-applicant-select-${applicant.userPublicId}`}
              />
            )
          case ColumnId.Applicant:
            return (
              <ImageCell
                key={column.id}
                title={applicant.displayName}
                image={applicant.avatar}
              />
            )
          case ColumnId.Location:
            return (
              <TextCell alignment={column.align} key={column.id}>
                {applicant.provider.stateRegion}
              </TextCell>
            )
          case ColumnId.Experience:
            return (
              <TextCell alignment={column.align} key={column.id}>
                {applicant.provider.yearsExperience}
              </TextCell>
            )
          // TODO: We want to display number of active workspaces, if possible.
          case ColumnId.TotalWorkspaces:
            return (
              <TextCell alignment={column.align} key={column.id}>
                {applicant.provider.workspaces.length}
              </TextCell>
            )
          case ColumnId.GlobalCapacity:
            return (
              <TextCell alignment={column.align} key={column.id}>
                {globalCapacityString}
              </TextCell>
            )
          case ColumnId.Status:
            return <StatusCell key={column.id} applicant={applicant} />
          case ColumnId.Action:
            return <ActionCell vm={vm} applicant={applicant} key={column.id} />
          default:
            return <TableCell key={column.id} />
        }
      })}
    </TableRow>
  )
})

interface StatusCellProps {
  applicant: WorkspaceApplicant
  dataTest?: string
}

const StatusCell = observer(function ({
  applicant,
  dataTest,
}: StatusCellProps) {
  const formattedStatus = useObserver(() => {
    switch (applicant.status) {
      case WorkspaceApplicantStatus.Accepted:
        return { text: 'Joined', className: styles.statusJoined }
      case WorkspaceApplicantStatus.Applied:
        return { text: 'Applied', className: styles.statusApplied }
      case WorkspaceApplicantStatus.Expired:
        return { text: 'Expired', className: styles.statusExpired }
      case WorkspaceApplicantStatus.Invited:
        return { text: 'Invited', className: styles.statusInvited }
      case WorkspaceApplicantStatus.Rejected:
        return { text: 'Declined', className: styles.statusDeclined }
      case WorkspaceApplicantStatus.Withdrawn:
        return { text: 'Withdrawn', className: styles.statusWithdrawn }
      case WorkspaceApplicantStatus.Null:
        return { text: 'Unspecified' }
    }
  })

  return (
    <TableCell className={cx(styles.statusCell, formattedStatus.className)}>
      <span data-test={dataTest}>{formattedStatus.text}</span>
    </TableCell>
  )
})

interface ActionCellProps {
  vm: ApplicantManagementPanelViewModel
  applicant: WorkspaceApplicant
}

const ActionCell = observer(function ({ vm, applicant }: ActionCellProps) {
  return (
    <TableCell className={styles.actionCell}>
      <Dropdown
        autoDismiss
        width="miniscule"
        position="bottom-end"
        button={
          <Button
            size="baseline"
            type="text"
            dataTest={`applicant-manager-applicant-actions-open-${applicant.userPublicId}`}
          >
            <Icon color="var(--grey800)" name="more" size={24} />
          </Button>
        }
      >
        <div>
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onClick={() => vm.inviteApplicant(applicant)}
            disabled={
              applicant.status === WorkspaceApplicantStatus.Invited ||
              applicant.status === WorkspaceApplicantStatus.Accepted ||
              vm.remainingCapacity < 1
            }
            dataTest={`applicant-manager-applicant-actions-invite-${applicant.userPublicId}`}
          >
            Invite
          </DropdownItem>

          <DropdownItem
            onClick={() => vm.rescindInvitation(applicant)}
            disabled={applicant.status !== WorkspaceApplicantStatus.Invited}
            dataTest={`applicant-manager-applicant-actions-rescind-invitation-${applicant.userPublicId}`}
          >
            Rescind
          </DropdownItem>
        </div>
      </Dropdown>
    </TableCell>
  )
})
